import type { ReactElement } from 'react'
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings'
import CloseIcon from '@mui/icons-material/Close'
import LogoutIcon from '@mui/icons-material/Logout'
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone'
import PersonIcon from '@mui/icons-material/Person'
import type { SvgIconComponent } from '@mui/icons-material'
import {
  Box,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography
} from '@mui/material'
import { alpha, useTheme } from '@mui/material/styles'
import { Link as RouterLink } from 'react-router-dom'
import logoImage from '../../assets/svetobur-logo.png'
import CountPill from '../CountPill'
import { NOTIFICATIONS_TEXT, formatNotificationsLabel } from '../notifications/notificationsText'
import UserAvatar from './UserAvatar'

export interface AppNavItem {
  key: string
  path: string
  label: string
  icon: SvgIconComponent
}

export interface AppNavDrawerProps {
  open: boolean
  onClose: () => void
  sectionItems: readonly AppNavItem[]
  secondaryItems: readonly AppNavItem[]
  activeKey: string | null
  onNavigate: () => void
  onLogoClick: () => void
  displayName: string
  username: string
  initials: string
  showAdmin: boolean
  onProfile: () => void
  onAdmin: () => void
  onLogout: () => void
  /** Shows the «Уведомления» item that opens the notifications sheet. */
  onNotifications?: () => void
  notificationsUnreadCount?: number
}

export default function AppNavDrawer({
  open,
  onClose,
  sectionItems,
  secondaryItems,
  activeKey,
  onNavigate,
  onLogoClick,
  displayName,
  username,
  initials,
  showAdmin,
  onProfile,
  onAdmin,
  onLogout,
  onNotifications,
  notificationsUnreadCount = 0
}: AppNavDrawerProps): ReactElement {
  const theme = useTheme()
  const itemSx = {
    minHeight: 48,
    borderRadius: 1,
    px: 2,
    '& .MuiListItemIcon-root': { minWidth: 36, color: 'text.secondary' },
    '& .MuiSvgIcon-root': { fontSize: 22 },
    '&.Mui-selected, &.Mui-selected:hover': {
      bgcolor: alpha(theme.palette.primary.main, 0.12),
      color: 'primary.main'
    },
    '&.Mui-selected .MuiListItemIcon-root': { color: 'primary.main' }
  }
  const textSlotProps = { primary: { sx: { fontWeight: 500 } } }

  function renderLinkItem(item: AppNavItem) {
    const Icon = item.icon
    const selected = item.key === activeKey
    return (
      <ListItemButton
        key={item.key}
        component={RouterLink}
        to={item.path}
        selected={selected}
        aria-current={selected ? 'page' : undefined}
        onClick={onNavigate}
        sx={itemSx}
      >
        <ListItemIcon><Icon /></ListItemIcon>
        <ListItemText primary={item.label} slotProps={textSlotProps} />
      </ListItemButton>
    )
  }

  function renderActionItem(key: string, label: string, Icon: SvgIconComponent, onClick: () => void) {
    const selected = key === activeKey
    return (
      <ListItemButton
        selected={selected}
        aria-current={selected ? 'page' : undefined}
        onClick={onClick}
        sx={itemSx}
      >
        <ListItemIcon><Icon /></ListItemIcon>
        <ListItemText primary={label} slotProps={textSlotProps} />
      </ListItemButton>
    )
  }

  return (
    <Drawer
      variant="temporary"
      anchor="left"
      open={open}
      onClose={onClose}
      ModalProps={{ keepMounted: false }}
      slotProps={{ paper: { 'aria-label': 'Меню приложения', sx: { width: 300, maxWidth: '85vw' } } }}
    >
      <Box
        component="nav"
        aria-label="Разделы"
        sx={{ display: 'flex', flexDirection: 'column', minHeight: '100%', pt: 1, px: 1, pb: 2 }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pt: 0.5, px: 1, pb: 1 }}>
          <Box
            component="img"
            src={logoImage}
            alt="Светобур"
            onClick={onLogoClick}
            sx={{ width: 80, objectFit: 'contain', cursor: 'pointer' }}
          />
          <IconButton aria-label="Закрыть меню" onClick={onClose} sx={{ width: 44, height: 44, borderRadius: 1 }}>
            <CloseIcon sx={{ fontSize: 22 }} />
          </IconButton>
        </Box>

        <List disablePadding>{sectionItems.map(renderLinkItem)}</List>
        <Divider sx={{ my: 1, mx: 1 }} />
        <List disablePadding>
          {onNotifications && (
            <ListItemButton
              aria-label={formatNotificationsLabel(notificationsUnreadCount)}
              aria-haspopup="dialog"
              onClick={onNotifications}
              sx={itemSx}
            >
              <ListItemIcon><NotificationsNoneIcon /></ListItemIcon>
              <ListItemText primary={NOTIFICATIONS_TEXT.title} slotProps={textSlotProps} />
              {notificationsUnreadCount > 0 && <CountPill count={notificationsUnreadCount} tone="unread" />}
            </ListItemButton>
          )}
          {secondaryItems.map(renderLinkItem)}
        </List>

        <Box sx={{ flex: 1 }} />
        <Divider sx={{ my: 1, mx: 1 }} />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1 }}>
          <UserAvatar initials={initials} size={32} />
          <Box sx={{ minWidth: 0 }}>
            <Typography noWrap title={displayName} sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
              {displayName}
            </Typography>
            {username && (
              <Typography noWrap title={username} variant="caption" component="div" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                {username}
              </Typography>
            )}
          </Box>
        </Box>

        <List disablePadding>
          {renderActionItem('profile', 'Профиль', PersonIcon, onProfile)}
          {showAdmin && renderActionItem('admin', 'Админка', AdminPanelSettingsIcon, onAdmin)}
          {renderActionItem('logout', 'Выход', LogoutIcon, onLogout)}
        </List>
      </Box>
    </Drawer>
  )
}
